import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { FileText, Search, Filter, Calendar } from 'lucide-react'
import { formatWithUTC } from '@/lib/datetime'

interface Organization {
  id: string;
  name: string;
}

interface LogsTabProps {
  organization: Organization;
}

interface LogEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  section: string;
  details: string;
  ip_address?: string;
}

export function LogsTab({ organization }: LogsTabProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [filterSection, setFilterSection] = useState('all')
  const [filterAction, setFilterAction] = useState('all')
  const [logs, setLogs] = useState<LogEntry[]>([])

  useEffect(() => {
    fetchAuditLogs()
  }, [])

  const fetchAuditLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('audit_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50)
      
      if (error) throw error
      
      const formattedLogs: LogEntry[] = (data || []).map(log => ({
        id: log.id,
        timestamp: formatWithUTC(log.created_at),
        user: 'Systemanvändare',
        action: log.action,
        section: log.table_name,
        details: JSON.stringify(log.details),
        ip_address: 'N/A'
      }))
      
      setLogs(formattedLogs)
    } catch (error) {
      console.error('Error fetching audit logs:', error)
      setLogs([])
    }
  }

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.details.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesSection = filterSection === 'all' || log.section === filterSection
    const matchesAction = filterAction === 'all' || log.action === filterAction

    return matchesSearch && matchesSection && matchesAction
  })

  const getActionBadge = (action: string) => {
    const variants = {
      'Uppdaterad': 'default',
      'Åtkomst': 'secondary',
      'Skapad': 'default',
      'Automatisk': 'outline'
    } as const

    return (
      <Badge variant={variants[action as keyof typeof variants] || 'secondary'}>
        {action}
      </Badge>
    )
  }

  const sections = [
    'Allmänna inställningar',
    'Notifieringar',
    'Roller & behörighet',
    'Gallring & arkiv',
    'Billing',
    'Export',
    'Säkerhet'
  ]

  const actions = ['Åtkomst', 'Uppdaterad', 'Skapad', 'Automatisk']

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Inställningsloggar
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Alla åtkomster och ändringar av organisationsinställningar
          </p>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1">
              <Label htmlFor="search" className="sr-only">Sök</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 text-muted-foreground transform -translate-y-1/2" />
                <Input
                  id="search"
                  placeholder="Sök användare, åtgärd eller detaljer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="section-filter" className="sr-only">Filtrera sektion</Label>
              <Select value={filterSection} onValueChange={setFilterSection}>
                <SelectTrigger className="w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla sektioner</SelectItem>
                  {sections.map((section) => (
                    <SelectItem key={section} value={section}>
                      {section}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="action-filter" className="sr-only">Filtrera åtgärd</Label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alla åtgärder</SelectItem>
                  {actions.map((action) => (
                    <SelectItem key={action} value={action}>
                      {action}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Tidpunkt
                    </div>
                  </TableHead>
                  <TableHead>Användare</TableHead>
                  <TableHead>Åtgärd</TableHead>
                  <TableHead>Sektion</TableHead>
                  <TableHead>Detaljer</TableHead>
                  <TableHead className="w-32">IP-adress</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      Inga loggar hittades som matchar dina filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-xs">
                        {log.timestamp}
                      </TableCell>
                      <TableCell className="font-medium">
                        {log.user}
                      </TableCell>
                      <TableCell>
                        {getActionBadge(log.action)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {log.section}
                      </TableCell>
                      <TableCell className="text-sm max-w-md">
                        {log.details}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {log.ip_address === 'system' ? 'System' : log.ip_address}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {filteredLogs.length > 0 && (
            <p className="text-sm text-muted-foreground mt-4">
              Visar {filteredLogs.length} av {logs.length} loggposter.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}